import { validateEnvVarKey } from './deployPlan.ts';

export type WizardStep = 'source' | 'target' | 'configure' | 'review';
export const WIZARD_STEPS: readonly WizardStep[] = ['source', 'target', 'configure', 'review'];

export interface WizardState {
  step: WizardStep;
  catalogId: number | null;
  stackName: string;
  endpointId: number | null; // null = auto-select
  domain: string; // '' = the default domain
  subdomain: string; // '' = generated name
  appName: string;
  externalUserId: string;
  displayName: string;
  useCustomName: boolean;
  instanceName: string;
  envVars: Readonly<Record<string, string>>; // effective values (defaults merged with edits)
  envDefaults: Readonly<Record<string, string>>; // defaults of the selected stack
  maxStep: number; // furthest step index reached
}

export type WizardAction =
  | { type: 'selectCatalog'; catalogId: number | null }
  | { type: 'selectStack'; stackName: string; envDefaults: Record<string, string> | null; allowedEndpointIds: Set<number> | null }
  | { type: 'selectEndpoint'; endpointId: number | null }
  | { type: 'selectDomain'; domain: string }
  | { type: 'setSubdomain'; value: string }
  | { type: 'setField'; field: 'appName' | 'externalUserId' | 'displayName' | 'instanceName'; value: string }
  | { type: 'setUseCustomName'; value: boolean }
  | { type: 'setEnv'; key: string; value: string }
  | { type: 'addEnv'; key: string; value: string }
  | { type: 'removeEnv'; key: string }
  | { type: 'goTo'; step: WizardStep }
  | { type: 'next'; canAdvance: boolean }
  | { type: 'back' };

export const initialWizardState: WizardState = {
  step: 'source',
  catalogId: null,
  stackName: '',
  endpointId: null,
  domain: '',
  subdomain: '',
  appName: '',
  externalUserId: '',
  displayName: '',
  useCustomName: false,
  instanceName: '',
  envVars: {},
  envDefaults: {},
  maxStep: 0,
};

export function stepIndex(s: WizardStep): number {
  return WIZARD_STEPS.indexOf(s);
}

const has = (o: Readonly<Record<string, string>>, k: string) => Object.prototype.hasOwnProperty.call(o, k);

const sameRecord = (a: Readonly<Record<string, string>>, b: Readonly<Record<string, string>>): boolean => {
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  return ak.every((k) => has(b, k) && a[k] === b[k]);
};

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'selectCatalog':
      if (action.catalogId === state.catalogId) return state;
      return {
        ...state,
        step: 'source',
        catalogId: action.catalogId,
        stackName: '',
        envVars: {},
        envDefaults: {},
        endpointId: null,
        maxStep: 0,
      };

    // Dispatch only on user selection — never from refetch effects. Changed defaults for the same stack name intentionally reset env edits.
    case 'selectStack': {
      const defaults = action.envDefaults ?? {};
      const keepEndpoint =
        action.allowedEndpointIds === null ||
        state.endpointId === null ||
        action.allowedEndpointIds.has(state.endpointId);
      if (action.stackName === state.stackName && sameRecord(defaults, state.envDefaults)) {
        // Dropping the endpoint invalidates Target, so progress restarts at Source.
        return keepEndpoint ? state : { ...state, endpointId: null, step: 'source', maxStep: 0 };
      }
      // A different stack invalidates Target/Configure gating: the user must re-walk them.
      return {
        ...state,
        step: 'source',
        maxStep: 0,
        stackName: action.stackName,
        envVars: { ...defaults },
        envDefaults: { ...defaults },
        endpointId: keepEndpoint ? state.endpointId : null,
      };
    }

    case 'selectEndpoint':
      return action.endpointId === state.endpointId ? state : { ...state, endpointId: action.endpointId };

    // Independent of catalog, stack and endpoint: kept when they change.
    case 'selectDomain':
      return action.domain === state.domain ? state : { ...state, domain: action.domain };

    // Like the domain, kept when catalog or stack change; the page ignores it when the app gets no hostname.
    case 'setSubdomain': {
      const value = action.value.toLowerCase();
      return value === state.subdomain ? state : { ...state, subdomain: value };
    }

    case 'setField': {
      const value =
        action.field === 'appName' || action.field === 'instanceName' ? action.value.toLowerCase() : action.value;
      return state[action.field] === value ? state : { ...state, [action.field]: value };
    }

    case 'setUseCustomName':
      return state.useCustomName === action.value ? state : { ...state, useCustomName: action.value };

    case 'setEnv':
      if (!has(state.envVars, action.key) || state.envVars[action.key] === action.value) return state;
      return { ...state, envVars: { ...state.envVars, [action.key]: action.value } };

    case 'addEnv':
      if (!validateEnvVarKey(action.key) || has(state.envVars, action.key)) return state;
      return { ...state, envVars: { ...state.envVars, [action.key]: action.value } };

    case 'removeEnv': {
      if (!has(state.envVars, action.key) || has(state.envDefaults, action.key)) return state;
      const envVars = { ...state.envVars };
      delete envVars[action.key];
      return { ...state, envVars };
    }

    case 'goTo': {
      const i = stepIndex(action.step);
      if (i < 0 || i > state.maxStep || action.step === state.step) return state;
      return { ...state, step: action.step };
    }

    case 'next': {
      const i = stepIndex(state.step);
      if (!action.canAdvance || i >= WIZARD_STEPS.length - 1) return state;
      const n = i + 1;
      return { ...state, step: WIZARD_STEPS[n], maxStep: Math.max(state.maxStep, n) };
    }

    case 'back': {
      const i = stepIndex(state.step);
      return i <= 0 ? state : { ...state, step: WIZARD_STEPS[i - 1] };
    }

    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}
